'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Crown, 
  Check,
  Spinner,
  ArrowRight,
  Receipt,
  CreditCard,
  Warning,
} from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { useRuntimeConfigStore } from '../stores/runtimeConfigStore';
import { subscriptionApi, paymentApi } from '../services/api';
import { getRegionProfile } from '../config/region';
import { useToastActions } from '../components/ui/Toast';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

// 格式化字节
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// 计划等级顺序（用于判断是否可升级）
const planLevels: Record<string, number> = {
  basic: 0,
  free: 0,
  pro: 1,
  team: 2,
  enterprise: 3,
};

// 计划价格映射（月费）
// Basic: $5/月, Pro: $13/月, Team: $39/月（展示用）
const planPrices: Record<string, number> = {
  basic: 9.9,
  free: 9.9,
  pro: 19.9,
  team: 49.9,
  enterprise: 0, // 联系销售
};

// 账单记录类型
interface BillingRecord {
  id: string;
  date: string;
  amount: number;
  status: string;
  plan?: string;
  description?: string;
  outTradeNo?: string;
}

// 计划详情类型
interface PlanDetails {
  plan: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
  name: string;
  maxWorkflowExecutions: number;
  maxStorageBytes: number;
  features: string[];
}

export function SubscriptionPage() {
  const { t, i18n } = useTranslation();
  const { error: toastError } = useToastActions();
  const { subscription, fetchSubscription } = useSubscriptionStore();
  const { isRegionGlobal, fetchConfig, config } = useRuntimeConfigStore();
  const profile = getRegionProfile();
  const isCN = profile.region === 'cn';
  const [usageStats, setUsageStats] = useState<{
    executionUsagePercent: number;
    storageUsagePercent: number;
  } | null>(null);
  const [currentPlanDetails, setCurrentPlanDetails] = useState<PlanDetails | null>(null);
  const [billingRecords, setBillingRecords] = useState<BillingRecord[]>([]);
  const [paymentSub, setPaymentSub] = useState<{
    active?: boolean;
    hasSubscription?: boolean;
    provider?: string;
  } | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true);

  // 待支付订单列表
  const [pendingOrders, setPendingOrders] = useState<Array<{
    id: string;
    plan: string;
    durationMonths: number;
    status: string;
    expiresAt: string;
    checkoutUrl?: string;
    provider?: string;
  }>>([]);

  // 确认对话框状态
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReuseOrderDialog, setShowReuseOrderDialog] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [matchedOrder, setMatchedOrder] = useState<{
    id: string;
    checkoutUrl?: string;
    provider?: string;
  } | null>(null);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  // 订阅或语言变化时，拉取与界面语言一致的计划功能列表（避免英文界面仍显示中文特性）
  useEffect(() => {
    if (!subscription) return;
    const lang = i18n.language || 'en';
    subscriptionApi.getAllPlans(lang).then((res) => {
      if (res.code === 0 && res.data) {
        const currentPlan = res.data.find((p) => p.plan === subscription.plan);
        if (currentPlan) {
          setCurrentPlanDetails(currentPlan);
        }
      }
    });
  }, [subscription, i18n.language]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchConfig(),
        fetchSubscription(),
        subscriptionApi.getUsageStats().then((res) => {
          if (res.code === 0) setUsageStats(res.data);
        }),
        subscriptionApi.getBillingHistory().catch(() => ({ code: 0, data: [] })).then((billingRes) => {
          if (billingRes?.code === 0 && billingRes?.data) {
            setBillingRecords(billingRes.data);
          }
        }),
        paymentApi.getSubscriptionStatus().then((res) => {
          if (res.code === 0 && res.data) setPaymentSub(res.data);
        }),
        // 获取待支付订单列表
        subscriptionApi.getPendingOrders().then((res) => {
          if (res.code === 0 && res.data) {
            setPendingOrders(res.data);
          }
        }).catch(() => {
          setPendingOrders([]);
        }),
      ]);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 检查是否可以升级（当前不是最高级别计划）
  const canUpgrade = () => {
    const currentPlan = subscription?.plan || 'free';
    return planLevels[currentPlan] < planLevels['enterprise'];
  };

  /** 是否已有正常生效的付费订阅（无需再点 Get It 购买） */
  const isNormallySubscribed = () => {
    const plan = subscription?.plan ?? 'free';
    const status = subscription?.status;
    if (status === 'active' && plan !== 'free') {
      return true;
    }
    if (paymentSub?.active && paymentSub?.hasSubscription) {
      return true;
    }
    return false;
  };

  // 检查是否有匹配的待支付旧订单
  const findMatchingPendingOrder = (billingCycle: 'monthly' | 'yearly') => {
    const durationMonths = billingCycle === 'yearly' ? 12 : 1;
    const now = new Date().toISOString();
    
    return pendingOrders.find(order => {
      // 同一计划
      if (order.plan !== currentPlan) return false;
      // 同一计费周期
      if (order.durationMonths !== durationMonths) return false;
      // 待支付状态
      if (order.status !== 'pending') return false;
      // 未过期
      if (order.expiresAt && order.expiresAt < now) return false;
      return true;
    });
  };

  // 直接处理订阅支付（月付/年付）
  const handleSubscribe = async (billingCycle: 'monthly' | 'yearly') => {
    const durationMonths = billingCycle === 'yearly' ? 12 : 1;
    
    // 先检查是否有匹配的待支付旧订单
    const matchingOrder = findMatchingPendingOrder(billingCycle);
    if (matchingOrder) {
      setSelectedBillingCycle(billingCycle);
      setMatchedOrder({
        id: matchingOrder.id,
        checkoutUrl: matchingOrder.checkoutUrl,
        provider: matchingOrder.provider,
      });
      setShowReuseOrderDialog(true);
      return;
    }
    
    // 没有旧订单，创建新订单
    await createNewSubscription(durationMonths);
  };

  // 创建新订阅订单
  const createNewSubscription = async (durationMonths: number) => {
    try {
      // 获取支付提供商状态
      const providerRes = await paymentApi.getSubscriptionStatus();
      const provider = providerRes.data?.provider;
      
      // 调用订阅升级 API
      const res = await subscriptionApi.upgradeSubscription(currentPlan, durationMonths, provider);
      
      if (res.code === 0 && res.data?.checkoutUrl) {
        // 跳转到支付页面
        window.location.assign(res.data.checkoutUrl);
      } else {
        // 显示错误消息（包含 429 限流错误）
        const errorMessage = res.message || 'Failed to create checkout';
        toastError(errorMessage);
      }
    } catch (error: unknown) {
      // 处理 HTTP 错误（如 429 Too Many Requests）
      const err = error as { response?: { status?: number; data?: { message?: string } } };
      if (err.response?.status === 429) {
        const message = err.response.data?.message || 'Too many requests. Please try again later.';
        toastError(message);
      } else {
        const msg = err.response?.data?.message || 'Subscription error';
        toastError(msg);
      }
    }
  };

  // 使用旧订单继续支付
  const handleReuseOrder = () => {
    if (matchedOrder?.checkoutUrl) {
      window.location.assign(matchedOrder.checkoutUrl);
    }
    setShowReuseOrderDialog(false);
  };

  // 创建新订单（用户拒绝使用旧订单）
  const handleCreateNewOrder = async () => {
    setShowReuseOrderDialog(false);
    const durationMonths = selectedBillingCycle === 'yearly' ? 12 : 1;
    await createNewSubscription(durationMonths);
  };

  const performCancelSubscription = async () => {
    if (!paymentSub?.hasSubscription) return;
    setCancelLoading(true);
    try {
      const provider = paymentSub.provider;
      let res;
      if (provider === 'lemonsqueezy') {
        res = await subscriptionApi.cancelLemonSqueezySubscription();
      } else if (provider === 'creem' || provider === 'paddle' || provider === 'stripe') {
        res = await paymentApi.cancelSubscription(provider);
      } else {
        res = await paymentApi.cancelSubscription();
      }
      if (res && (res.code === 0 || res.code === 200)) {
        await fetchSubscription();
        const st = await paymentApi.getSubscriptionStatus();
        if (st.code === 0 && st.data) setPaymentSub(st.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCancelLoading(false);
    }
  };

  // 获取计划功能的翻译
  const getFeatureTranslation = (feature: string): string => {
    // 尝试匹配常见的功能描述模式并翻译
    
    // 执行次数相关
    const execMatch = feature.match(/(\d+)\s*次.*执行|executions.*?(\d+)/i);
    if (execMatch) {
      const count = parseInt(execMatch[1] || execMatch[2]);
      if (feature.includes('无限') || feature.includes('unlimited')) {
        return t('settings.planFeaturesList.unlimitedExecutions');
      }
      return t('settings.planFeaturesList.executions', { count });
    }
    if (feature.includes('无限') || feature.includes('unlimited')) {
      return t('settings.planFeaturesList.unlimitedExecutions');
    }

    // AI 调用次数相关（支持中文/英文，兼容带逗号的千分位）
    const isAiFeature = /AI|ai/i.test(feature) || feature.includes('调用');
    if (isAiFeature) {
      const unlimitedAi = feature.includes('无限') || feature.includes('unlimited');
      if (unlimitedAi) return t('settings.planFeaturesList.unlimitedAICalls');

      const aiMatch =
        feature.match(/(\d[\d,]*)\s*次.*AI/i) ||
        feature.match(/AI.*?(\d[\d,]*)/i) ||
        feature.match(/(\d[\d,]*)\s*AICalls/i) ||
        feature.match(/ai calls.*?(\d[\d,]*)/i);
      if (aiMatch) {
        const raw = (aiMatch[1] || aiMatch[2] || '').replace(/,/g, '');
        const count = parseInt(raw);
        if (!Number.isNaN(count)) return t('settings.planFeaturesList.aiCalls', { count });
      }
    }
    
    // 存储相关
    const storageMatch = feature.match(/(\d+)(GB|MB|TB).*存储|storage.*?(\d+)/i);
    if (storageMatch) {
      const size = storageMatch[1] + storageMatch[2] || storageMatch[3];
      return t('settings.planFeaturesList.storage', { size });
    }
    
    // 团队成员相关
    const teamMatch =
      feature.match(/最多\s*(\d[\d,]*)\s*名.*成员/) ||
      feature.match(/每个团队.*?(\d[\d,]*)\s*名/) ||
      feature.match(/up to\s*(\d[\d,]*)\s*members\s*per\s*team/i);
    if (teamMatch) {
      const raw = (teamMatch[1] || '').replace(/,/g, '');
      const count = parseInt(raw);
      if (!Number.isNaN(count)) return t('settings.planFeaturesList.teamMembers', { count });
    }
    
    // 基础/高级数据处理
    if (feature.includes('基础数据处理') || feature.includes('basic')) {
      return t('settings.planFeaturesList.basicProcessing');
    }
    if (feature.includes('高级数据处理') || feature.includes('advanced')) {
      return t('settings.planFeaturesList.advancedProcessing');
    }
    
    // Excel/CSV 支持
    if (feature.includes('excel') || feature.includes('csv')) {
      return t('settings.planFeaturesList.excelCsvSupport');
    }
    
    // 社区支持
    if (feature.includes('社区') || feature.includes('community')) {
      return t('settings.planFeaturesList.communitySupport');
    }
    
    // 优先支持
    if (feature.includes('优先') || feature.includes('priority')) {
      return t('settings.planFeaturesList.prioritySupport');
    }
    
    // 专属支持
    if (feature.includes('专属') || feature.includes('dedicated')) {
      return t('settings.planFeaturesList.dedicatedSupport');
    }
    
    // API 访问
    if (feature.includes('api')) {
      return t('settings.planFeaturesList.apiAccess');
    }
    
    // 保存模板
    if (feature.includes('模板') || feature.includes('template')) {
      return t('settings.planFeaturesList.saveAsTemplate');
    }
    
    // 默认返回原始文本
    return feature;
  };

  // 获取状态样式
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'paid':
        return 'text-emerald-600 bg-emerald-50';
      case 'refunded':
        return 'text-amber-600 bg-amber-50';
      case 'failed':
        return 'text-red-600 bg-red-50';
      case 'pending':
      case 'unpaid':
        return 'text-blue-600 bg-blue-50';
      case 'closed':
        return 'text-zinc-500 bg-zinc-100';
      default:
        return 'text-zinc-600 bg-zinc-50';
    }
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'paid':
        return t('settings.statusPaid');
      case 'refunded':
        return t('settings.statusRefunded');
      case 'failed':
        return t('settings.statusFailed');
      case 'pending':
      case 'unpaid':
        return t('settings.statusPending');
      case 'closed':
        return t('settings.statusClosed');
      default:
        return status;
    }
  };

  // 获取计划名称
  const getPlanName = (plan?: string) => {
    if (!plan) return '';
    return t(`nav.planNames.${plan}`) || plan;
  };

  if (loading) {
    return (
      <div className="h-full bg-zinc-50 flex items-center justify-center">
        <Spinner className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  const currentPlan = subscription?.plan || 'free';
  const currentPrice = planPrices[currentPlan];

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        className="max-w-3xl mx-auto p-6"
      >
        {/* 页面标题 */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">{t('settings.subscriptionTitle')}</h1>
            <p className="text-sm text-zinc-500 mt-1">{t('settings.subscriptionSubtitle')}</p>
          </div>
          {/* 订阅按钮 - 仅在国际区域显示；已正常订阅则隐藏 */}
          {canUpgrade() && isRegionGlobal() && !isNormallySubscribed() && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSubscribe('monthly')}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Get It (Monthly)
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleSubscribe('yearly')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Get It (Yearly)
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* 当前计划信息卡片 */}
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl border border-emerald-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-600">{t('settings.currentPlan')}</p>
                <p className="text-2xl font-bold text-zinc-900">{getPlanName(currentPlan)}</p>
                {/* CN区域隐藏所有计划价格 */}
                {!isCN && (
                  <p className="text-lg font-semibold text-emerald-600 mt-1">
                    {`$${currentPrice}${t('settings.monthly')}`}
                  </p>
                )}
                {subscription?.expiresAt && (
                  <p className="text-xs text-zinc-500 mt-1">
                    {t('settings.expireDate')}：{new Date(subscription.expiresAt).toLocaleDateString('zh-CN')}
                  </p>
                )}
              </div>
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <Crown className="w-7 h-7 text-emerald-600" weight="fill" />
              </div>
            </div>
          </div>

          {/* 用量统计 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <h3 className="text-sm font-medium text-zinc-900 mb-4">{t('settings.usage')}</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-zinc-600">{t('settings.workflowExecutions')}</span>
                  <span className="text-zinc-900 font-medium">
                    {subscription?.currentMonthExecutions || 0} / {subscription?.maxWorkflowExecutions === -1 ? t('settings.unlimited') : subscription?.maxWorkflowExecutions || 100}
                  </span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all" 
                    style={{ width: `${Math.min(usageStats?.executionUsagePercent || 0, 100)}%` }} 
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-zinc-600">{t('settings.dataStorage')}</span>
                  <span className="text-zinc-900 font-medium">
                    {formatBytes(subscription?.currentStorageBytes || 0)} / {formatBytes(subscription?.maxStorageBytes || 0)}
                  </span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all" 
                    style={{ width: `${Math.min(usageStats?.storageUsagePercent || 0, 100)}%` }} 
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 当前计划功能特性 */}
          {currentPlanDetails && currentPlanDetails.features.length > 0 && (
            <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
              <h3 className="text-sm font-medium text-zinc-900 mb-4">{t('settings.planFeatures')}</h3>
              <ul className="space-y-3">
                {currentPlanDetails.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm text-zinc-700">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" weight="bold" />
                    </div>
                    {getFeatureTranslation(feature)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 限制信息 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <h3 className="text-sm font-medium text-zinc-900 mb-4">{t('settings.planLimits')}</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">{t('settings.maxWorkflowExecutions')}</span>
                <span className="text-sm font-medium text-zinc-900">
                  {subscription?.maxWorkflowExecutions === -1 ? t('settings.unlimited') : `${subscription?.maxWorkflowExecutions || 100} ${t('settings.perMonth')}`}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">{t('settings.maxStorage')}</span>
                <span className="text-sm font-medium text-zinc-900">{formatBytes(subscription?.maxStorageBytes || 0)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">{t('common.status')}</span>
                <span className={`text-sm font-medium ${
                  subscription?.status === 'active' ? 'text-emerald-600' : 'text-amber-600'
                }`}>
                  {subscription?.status === 'active' ? t('common.active') : subscription?.status === 'expired' ? t('common.expired') : t('common.cancel')}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <span className="text-sm text-zinc-600">{t('settings.expireDate')}</span>
                <span className="text-sm font-medium text-zinc-900">
                  {subscription?.expiresAt 
                    ? new Date(subscription.expiresAt).toLocaleDateString('zh-CN')
                    : t('settings.unlimited')}
                </span>
              </div>
            </div>
          </div>

          {/* 账单展示名、客服、一键取消（国际区付费订阅） */}
          {(config?.billingDescriptor ||
            config?.supportUrl ||
            (isRegionGlobal() && paymentSub?.hasSubscription)) && (
            <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 space-y-4">
              <h3 className="text-sm font-medium text-zinc-900">{t('settings.billingSupportSection')}</h3>
              {config?.billingDescriptor && (
                <div className="flex items-start gap-3 text-sm text-zinc-600">
                  <CreditCard className="w-5 h-5 text-zinc-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-zinc-500">{t('settings.cardStatementDescriptor')}</p>
                    <p className="font-medium text-zinc-900 mt-0.5">{config.billingDescriptor}</p>
                  </div>
                </div>
              )}
              {config?.supportUrl && (
                <a
                  href={config.supportUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex text-sm font-medium text-emerald-600 hover:text-emerald-700 underline-offset-2 hover:underline"
                >
                  {t('settings.contactSupport')}
                </a>
              )}
              {isRegionGlobal() && paymentSub?.hasSubscription && (
                <div>
                  <button
                    type="button"
                    disabled={cancelLoading}
                    onClick={() => setShowCancelConfirm(true)}
                    className="text-sm text-zinc-600 hover:text-red-600 underline disabled:opacity-50"
                  >
                    {cancelLoading ? '…' : t('settings.cancelSubscription')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 账单历史 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-4 h-4 text-zinc-500" />
              <h3 className="text-sm font-medium text-zinc-900">{t('settings.billingHistory')}</h3>
            </div>
            <div className="max-h-80 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="text-left text-zinc-500 border-b border-zinc-200">
                    <th className="pb-3 font-medium">{t('settings.billingDate')}</th>
                    <th className="pb-3 font-medium">{t('common.description')}</th>
                    <th className="pb-3 font-medium text-right">{t('settings.billingAmount')}</th>
                    <th className="pb-3 font-medium text-right">{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {billingRecords.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-zinc-400 text-sm">
                        {t('settings.noBilling')}
                      </td>
                    </tr>
                  ) : (
                    billingRecords.map((row) => (
                      <tr key={row.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50">
                        <td className="py-3 text-zinc-900 whitespace-nowrap">
                          {new Date(row.date).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                        </td>
                        <td className="py-3 text-zinc-600">
                          <div className="flex flex-col">
                            <span>{row.description || (row.plan ? `${t('settings.upgradePlan')} - ${getPlanName(row.plan)}` : '-')}</span>
                            {row.outTradeNo && (
                              <span className="text-xs text-zinc-400">{row.outTradeNo}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-zinc-900 text-right font-medium">
                          ¥{Number(row.amount).toFixed(2)}
                        </td>
                        <td className="py-3 text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(row.status)}`}>
                            {getStatusText(row.status)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </motion.div>

      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          void performCancelSubscription();
        }}
        title={t('billing.cancelConfirmTitle')}
        description={t('billing.cancelConfirmDesc')}
        confirmText={t('billing.confirmCancel')}
        cancelText={t('common.cancel')}
        type="danger"
      />

      {/* 旧订单确认对话框 */}
      <Dialog.Root open={showReuseOrderDialog} onOpenChange={(open) => !open && setShowReuseOrderDialog(false)}>
        <Dialog.Portal>
          <AnimatePresence>
            {showReuseOrderDialog && (
              <>
                <Dialog.Overlay asChild>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
                  >
                    <Dialog.Title className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                      <Warning className="w-5 h-5 text-amber-500" weight="fill" />
                      {t('settings.pendingOrderExists')}
                    </Dialog.Title>
                    <Dialog.Description className="text-sm text-zinc-600 mb-6 leading-relaxed">
                      {t('settings.pendingOrderDesc', { cycle: selectedBillingCycle === 'yearly' ? t('settings.yearlyPay') : t('settings.monthlyPay') })}
                    </Dialog.Description>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={handleCreateNewOrder}
                        className="px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg"
                      >
                        {t('settings.createNewOrder')}
                      </button>
                      <button
                        onClick={handleReuseOrder}
                        className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      >
                        {t('settings.continuePayment')}
                      </button>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
